import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import { Cancel, Edit, Save } from '@mui/icons-material';
import { AppContext } from '../../../../../RoutesWithAuth';
import axios from '../../../../common/AxiosConfig';



const useStyles = makeStyles(() => ({
    tableWrapper: {
        marginTop: '2px',
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '8px',
            height: '10px'
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#a8a8a8',
            borderRadius: '4px',
            '&:hover': {
                background: '#888'
            },
        },
        '& table': {
            width: '100%',
            minWidth: '760px',
            tableLayout: 'fixed',
            borderCollapse: 'separate'
        },
        '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            background: '#C4D2E2',
        },
        '& th': {
            padding: '3px 16px',
            //textAlign: 'center',
            color: '#202124',
            fontWeight: 600,
            fontSize: '0.85rem',
            borderRight: '1px solid #c8cbce',
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
            background: '#C4D2E2',
            '&:last-child': {
                borderRight: 'none',
            },
        },
        '& .nameColumn': {
            width: '40%'
        },
        '& .narrowColumn': {
            width: '15%'
        },
        '& .providersColumn': {
            width: '20%'
        },
        '& .editColumn': {
            width: '10%',
            padding: '2px 4px',
            textAlign: 'center'
        },
        '& .MuiInputBase-root': {
            width: '100%',
            fontSize: '0.85rem'
        },
        '& .MuiSelect-select': {
            padding: '4px 28px 4px 8px'
        },
        '& tbody tr': {
            height: '4vh',
            background: '#f7fafd',
            fontWeight: 600,
            borderBottom: '1px solid #edf2f7',
            transition: 'background-color 0.2s ease',
            //background: '#dee2e6',
            '&:hover': {
                background: '#f0f7ff',
            },
        },
        '& td': {
            padding: '2px 16px',
            textAlign: 'left',
            color: '#334155',
            fontSize: '0.85rem',
            fontWeight: 600,
            borderRight: '1px solid #edf2f7',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '&:last-child': {
                borderRight: 'none',
            },
        },
    },
    saveButton: {
        color: '#62A9D4'
    },
    editButton: {
        color: '62A9D4'
    },
    cancelButton: {
        color: '#C00000'
    }
}));


const COLUMNS = [
    { key: 'name', label: 'Name', className: 'nameColumn' },
    { key: 'data_category', label: 'Data Category', className: 'narrowColumn' },
    { key: 'data_shared', label: 'Data Shared', className: 'narrowColumn' },
    { key: 'providers', label: 'Providers', className: 'providersColumn' },
    { key: 'edit', label: 'Edit', className: 'editColumn' },
];

const SELECT_OPTIONS = {
    data_category: ['Yes', 'No'],
    data_shared: ['Internal', 'External', 'Both'],
    providers: ['All', 'Dealers', 'Vendors', 'Service Providers']
};

const DataElementsTable = ({ taskId, dataElements = [] }) => {
    const classes = useStyles();
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const [rows, setRows] = useState(dataElements);
    const [editingRow, setEditingRow] = useState(null);
    const [draft, setDraft] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let isActive = true;
        setEditingRow(null);
        setDraft(null);
        setError('');
        setIsLoading(false);
        if (!taskId) return;

        setRows([]);
        setIsLoading(true);
        axios.get(`centra/privacy-task/${encodeURIComponent(taskId)}/data-elements`, {
            headers: { Authorization: authToken }
        }).then((response) => {
            if (isActive) setRows(response.data?.data_elements || []);
        }).catch((err) => {
            if (isActive) {
                setError(err?.response?.data?.message || err?.message || 'Unable to load data elements.');
            }
        }).finally(() => {
            if (isActive) setIsLoading(false);
        });

        return () => {
            isActive = false;
        };
    }, [taskId, authToken]);

    const cancelEdit = () => {
        setEditingRow(null);
        setDraft(null);
    };

    const saveEdit = async () => {
        if (!taskId || !draft || isSaving) return;

        const payload = {
            data_element_id: draft.data_element_id,
            data_category: draft.data_category || '',
            data_shared: draft.data_shared || '',
            providers: draft.providers || ''
        };
        setIsSaving(true);
        setError('');
        try {
            const response = await axios.put(`centra/privacy-task/${encodeURIComponent(taskId)}/data-elements`, payload, {
                headers: { Authorization: authToken }
            });
            const updated = response.data;
            setRows(previousRows => previousRows.map(row => row.data_element_id === payload.data_element_id ? {
                ...row,
                data_category: updated.data_category,
                data_shared: updated.data_shared,
                providers: updated.providers
            } : row));
            cancelEdit();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Unable to save data element.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderCell = (row, rowIndex, column) => {
        const isEditing = editingRow === rowIndex;
        if (column.key === 'edit') {
            return isEditing ? (
                <>
                    <Tooltip title="Save">
                        <IconButton className={classes.saveButton} size="small" aria-label={`Save ${row.name}`} onClick={saveEdit} disabled={isSaving}>
                            <Save fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                        <IconButton className={classes.cancelButton} size="small" aria-label={`Cancel ${row.name}`} onClick={cancelEdit} disabled={isSaving}>
                            <Cancel fontSize="small" color="error" />
                        </IconButton>
                    </Tooltip>
                </>
            ) : (
                <Tooltip title="Edit">
                    <span>
                        <IconButton
                            className={classes.editButton}
                            size="small"
                            aria-label={`Edit ${row.name}`}
                            disabled={editingRow !== null || !taskId}
                            onClick={() => { setError(''); setEditingRow(rowIndex); setDraft({ ...row }); }}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            );
        }
        if (isEditing && SELECT_OPTIONS[column.key]) {
            const value = draft[column.key] || '';
            const options = SELECT_OPTIONS[column.key];
            return (
                <Select
                    size="small"
                    value={value}
                    disabled={isSaving}
                    displayEmpty
                    inputProps={{ 'aria-label': `${column.label} for ${row.name}` }}
                    onChange={event => setDraft({ ...draft, [column.key]: event.target.value })}
                >
                    {/* <MenuItem value="">None</MenuItem> */}
                    {value && !options.includes(value) && <MenuItem value={value}>{value}</MenuItem>}
                    {options.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
            );
        }
        return row[column.key];
    };

    return (
        <div>
            {/* {isSaving && <p role="status">Saving data element...</p>} */}
            {error && <p role="alert">{error}</p>}
            {isLoading && <p role="status">Loading data elements...</p>}
            <div className={`tableWrapper ${classes.tableWrapper}`}>
                <table>
                    <thead>
                        <tr>
                            {COLUMNS.map((column) => <th className={column.className} key={column.key}>{column.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={row.data_element_id ?? rowIndex}>
                                {COLUMNS.map((column) => (
                                    <td className={column.className} key={column.key}>{renderCell(row, rowIndex, column)}</td>
                                ))}
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={COLUMNS.length}></td>
                        </tr>
                        <tr>
                            <td colSpan={COLUMNS.length}></td>
                        </tr>
                        {!isLoading && !error && rows.length === 0 && (
                            <tr><td colSpan={COLUMNS.length}>No data elements found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default DataElementsTable;